import React, { useCallback, useEffect, useState } from 'react';
import { useDepartment } from '../contexts/DepartmentContext';
import { usePageTitle } from '../contexts/PageHeaderContext';
import Tabs from '../components/ui/Tabs';
import SegmentedControl from '../components/ui/SegmentedControl';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import CrewTimesheet from '../components/timesheets/CrewTimesheet';
import ManagerTimesheets from '../components/timesheets/ManagerTimesheets';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import useDirtyPrompt from '../hooks/useDirtyPrompt';
import { UNSAVED_PROMPT_BUTTONS } from '../constants/unsavedChangesPrompt';
import { LIST_PAGE_SHELL } from '../constants/pageLayout';
import { toLocalISO } from '../utils/dateFormat';
import { fetchGags, fetchUnits } from '../utils/timesheetsApi';
import { mondayOf } from '../utils/timesheetDisplay';

/**
 * PLANNING → TIMESHEETS (stage S3b, owner rulings 2026-10-24,
 * `_wip/timesheets/TIMESHEETS-SPEC.md` §2).
 *
 * 🔑 ONE route, two screens, chosen by the CAPABILITY in the ACTIVE
 * department — `capabilities.canManageBudget` (spec: "anyone with budget
 * rights in that department, no new role"), never `isBuyer`, never a role
 * name. A manager gets the manager screen, with a "My timesheet" tab for
 * their own days when they are crew too; everyone else gets "My timesheet".
 * The choice is cosmetic — every manager route refuses without the
 * capability on the server.
 *
 * Unsaved work: TWO three-way prompts (`hooks/useDirtyPrompt`) — one over the
 * person-week grid of whichever screen is showing, one over the unit
 * timecard — and ONE leave guard over both. The Team / My timesheet switch
 * UNMOUNTS the manager screen, so it asks the day prompt and THEN the
 * timecard prompt; it never switches while either is dirty (S3b review R1).
 */

const TEAM = 'team';
const MINE = 'mine';

export default function Timesheets() {
  usePageTitle('Timesheets');
  const { activeDepartment, capabilities, registerDirtyEditGuard } = useDepartment();
  const departmentId = activeDepartment ? activeDepartment.department_id : null;
  const isManager = !!(capabilities && capabilities.canManageBudget === true);

  const [mode, setMode] = useState(TEAM);
  const [weekStart, setWeekStart] = useState(() => mondayOf(toLocalISO(new Date())));
  const [units, setUnits] = useState([]);
  const [gags, setGags] = useState([]);
  const [editorDirty, setEditorDirty] = useState(false);
  const [timecardDirty, setTimecardDirty] = useState(false);

  useEffect(() => {
    let live = true;
    if (!departmentId) return undefined;
    fetchUnits(departmentId).then((r) => { if (live && r.ok && Array.isArray(r.body)) setUnits(r.body); }).catch(() => {});
    fetchGags().then((r) => { if (live && r.ok && r.body && Array.isArray(r.body.gags)) setGags(r.body.gags); }).catch(() => {});
    return () => { live = false; };
  }, [departmentId]);

  const leaveGuard = useUnsavedChangesGuard({
    when: editorDirty || timecardDirty,
    message: "Your timesheet changes haven't been saved. If you leave, they will be lost.",
  });
  // A department switch remounts both screens (`key={departmentId}`) and does
  // not navigate, so the leave guard never sees it: the switcher asks only
  // when a page has registered its dirty state here (the order pages' rule).
  useEffect(() => {
    registerDirtyEditGuard?.(editorDirty || timecardDirty);
    return () => registerDirtyEditGuard?.(false);
  }, [editorDirty, timecardDirty, registerDirtyEditGuard]);
  const { continueWhenClean, saveRef, discardRef, promptProps } = useDirtyPrompt({ dirty: editorDirty });
  const timecard = useDirtyPrompt({ dirty: timecardDirty });
  // Day prompt first, then the timecard prompt (nobody can type in the
  // timecard while the day prompt is open, so its gate cannot go stale).
  const changeMode = (id) => continueWhenClean(() => timecard.continueWhenClean(() => setMode(id)));

  const onDirtyChange = useCallback((d) => setEditorDirty(!!d), []);
  const onTimecardDirtyChange = useCallback((d) => setTimecardDirty(!!d), []);

  const showManager = isManager && mode === TEAM;
  // The unit timecard unmounts with the manager screen; its dirty flag goes with it.
  useEffect(() => { if (!showManager) setTimecardDirty(false); }, [showManager]);
  // 📱 Below `md` the Team / My switch moves into the phone header's sheet (mock B, 2026-10-27).
  const modeControl = isManager ? (
    <SegmentedControl options={[{ id: TEAM, label: 'Team' }, { id: MINE, label: 'Mine' }]}
      value={mode} onChange={changeMode} ariaLabel="Timesheet" testId="timesheets-mode-phone" />
  ) : null;
  const shared = {
    weekStart, onWeekChange: setWeekStart, units, gags, continueWhenClean, saveRef, discardRef, onDirtyChange, modeControl,
  };

  return (
    <div className={LIST_PAGE_SHELL} data-testid="timesheets-page">
      {leaveGuard.dialog}
      <ConfirmDialog
        title="Unsaved changes to this day"
        message="This day has edits that have not been saved."
        {...UNSAVED_PROMPT_BUTTONS}
        variant="save"
        {...promptProps}
      />
      <ConfirmDialog
        title="Unsaved unit timecard"
        message="The unit timecard has entries that have not been saved."
        {...UNSAVED_PROMPT_BUTTONS}
        variant="save"
        {...timecard.promptProps}
      />

      {!departmentId ? (
        <EmptyState
          title="Choose a department"
          description="Timesheets belong to a department. Pick one from the switcher to see them."
          testId="timesheets-no-department"
        />
      ) : activeDepartment.timesheets_enabled !== true ? (
        // 🔒 Gated to DEMO departments until launch (owner 2026-10-28); the server refuses too.
        <EmptyState
          title="Timesheets are coming soon"
          description="Timesheets aren't switched on for this department yet."
          testId="timesheets-not-enabled"
        />
      ) : (
        <>
          {isManager && (
            <Tabs
              tabs={[{ id: TEAM, label: 'Team timesheets' }, { id: MINE, label: 'My timesheet' }]}
              active={mode}
              onChange={changeMode}
              idPrefix="timesheets-mode"
              ariaLabel="Timesheet view"
              className="mb-3 max-md:hidden"
            />
          )}
          {showManager ? (
            <ManagerTimesheets
              key={departmentId}
              {...shared}
              onTimecardDirtyChange={onTimecardDirtyChange}
              continueWhenTimecardClean={timecard.continueWhenClean}
              timecardSaveRef={timecard.saveRef}
              timecardDiscardRef={timecard.discardRef}
            />
          ) : (
            <CrewTimesheet key={departmentId} {...shared} />
          )}
        </>
      )}
    </div>
  );
}
